import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setDevOverride, resetDevOverrides } from '@app/store/slices/userSlice';
import {
  DEV_TOOLS_ENABLED,
  DEV_USER_STATES,
  DEV_NO_OVERRIDE,
} from '@shared/static/devUserStates';

/* Стили живут здесь, а не в sidebar.scss, чтобы в прод-сборку
   не попадало вообще ничего от переключателя — ни разметки, ни правил. */
const STYLE_ID = 'dev-switcher-styles';

const STYLES = `
.dev-switcher {
  --dev-accent: #e5a400;
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 5px 6px 6px;
  margin-bottom: 12px;
  border: 1px dashed var(--dev-accent);
  border-radius: 8px;
  background: var(--basic-component);
}
@media (max-width: 599.98px) {
  .dev-switcher { display: none; }
}
.dev-switcher__head {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 14px;
}
.dev-switcher__badge {
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.04em;
  padding: 1px 4px;
  border-radius: 4px;
  color: var(--white);
  background: var(--dev-accent);
}
.dev-switcher__reset {
  margin-left: auto;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  font-size: 10px;
  color: var(--text-secondary-color);
  border-bottom: 1px dashed currentColor;
}
.dev-switcher__reset:hover { color: var(--brand-primary-fill); }
.dev-switcher__row {
  display: flex;
  align-items: center;
  gap: 4px;
}
.dev-switcher__label {
  flex: 0 0 54px;
  font-size: 10px;
  line-height: 1.2;
  color: var(--text-secondary-color);
}
.dev-switcher__select {
  flex: 1 1 auto;
  min-width: 0;
  height: 22px;
  padding: 0 2px;
  border-radius: var(--control-radius);
  border: 1px solid var(--basic-border-color);
  background: var(--data-input-bg);
  color: var(--text-primary-color);
  font-family: var(--secondary-font);
  font-size: 11px;
  cursor: pointer;
}
.dev-switcher__select--active {
  border-color: var(--dev-accent);
  color: var(--dev-accent);
}
`;

const serialize = (value) => JSON.stringify(value ?? null);

export default function DevStateSwitcher() {
  const dispatch = useDispatch();
  const { devOverrides, apiUser } = useSelector((state) => state.user);

  useEffect(() => {
    if (!DEV_TOOLS_ENABLED || document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = STYLES;
    document.head.appendChild(style);
  }, []);

  if (!DEV_TOOLS_ENABLED) return null;

  const activeCount = Object.keys(devOverrides || {}).length;

  const handleChange = (field, raw) => {
    dispatch(
      setDevOverride({
        field,
        value: raw === DEV_NO_OVERRIDE ? undefined : JSON.parse(raw),
      }),
    );
  };

  return (
    <div className="dev-switcher">
      <div className="dev-switcher__head">
        <span className="dev-switcher__badge">DEV</span>
        {activeCount > 0 && (
          <button
            type="button"
            className="dev-switcher__reset"
            onClick={() => dispatch(resetDevOverrides())}
          >
            сброс
          </button>
        )}
      </div>

      {DEV_USER_STATES.map(({ field, label, short, options }) => {
        const isOverridden = devOverrides?.[field] !== undefined;
        const current = isOverridden
          ? serialize(devOverrides[field])
          : DEV_NO_OVERRIDE;

        return (
          <label className="dev-switcher__row" key={field} title={label}>
            <span className="dev-switcher__label">{short || label}</span>
            <select
              className={`dev-switcher__select ${
                isOverridden ? 'dev-switcher__select--active' : ''
              }`}
              value={current}
              onChange={(e) => handleChange(field, e.target.value)}
            >
              <option value={DEV_NO_OVERRIDE}>
                {field in (apiUser || {})
                  ? `с бэка (${String(apiUser[field] ?? 'null')})`
                  : 'с бэка'}
              </option>
              {options.map((option) => (
                <option key={option.label} value={serialize(option.value)}>
                  {option.short || option.label}
                </option>
              ))}
            </select>
          </label>
        );
      })}
    </div>
  );
}
